import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user, refreshUser } = useContext(AuthContext);

  const [status, setStatus] = useState(token ? 'verifying' : 'idle'); // verifying | success | error | idle | resending | resent
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await axios.post(`${API}/auth/verify-email-confirm`, { token });
        setStatus('success');
        // Refresh user state so emailVerified=true propagates
        if (refreshUser) refreshUser();
      } catch {
        setStatus('error');
      }
    })();
  }, [token]);

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-verify-email`, {});
      if (res.data.alreadyVerified) {
        toast.info('Your email is already verified.');
        if (refreshUser) refreshUser();
      } else {
        setStatus('resent');
        toast.success('Verification email sent');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else if (err.response?.status === 409 && detail?.code === 'DELIVERABILITY_BLOCKED') {
        toast.error('Cannot send: there is a delivery issue with your email address. Please contact support.');
      } else if (err.response?.status === 401) {
        toast.error('Please log in to resend verification email.');
      } else {
        toast.error('Failed to send verification email.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#FF6600' }}>
      <Card className="w-full max-w-md" data-testid="verify-email-card">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">Specard</CardTitle>
          <CardDescription className="text-center">Email Verification</CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'verifying' && (
            <div className="text-center py-6 space-y-3" data-testid="verify-loading">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
              <p className="text-sm text-gray-600">Verifying your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-6 space-y-4" data-testid="verify-success">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <p className="text-gray-700 font-medium">Your email has been verified.</p>
              <Link to={user ? '/dashboard' : '/'}>
                <Button className="mt-2" data-testid="verify-go-btn">
                  {user ? 'Go to Dashboard' : 'Go to Login'}
                </Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-6 space-y-4" data-testid="verify-error">
              <XCircle className="w-12 h-12 text-red-400 mx-auto" />
              <p className="text-gray-700">This verification link is invalid or expired.</p>
              {user ? (
                <Button
                  variant="outline"
                  onClick={handleResend}
                  disabled={resendLoading}
                  data-testid="verify-resend-btn"
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              ) : (
                <Link to="/" className="text-sm text-orange-600 hover:underline" data-testid="verify-login-link">
                  Log in to resend verification email
                </Link>
              )}
            </div>
          )}

          {status === 'idle' && (
            <div className="text-center py-6 space-y-4" data-testid="verify-idle">
              <p className="text-gray-600">Verify your email address to secure your account.</p>
              {user ? (
                <Button
                  onClick={handleResend}
                  disabled={resendLoading}
                  data-testid="verify-send-btn"
                >
                  {resendLoading ? 'Sending...' : 'Send Verification Email'}
                </Button>
              ) : (
                <Link to="/" className="text-sm text-orange-600 hover:underline">
                  Log in to verify your email
                </Link>
              )}
            </div>
          )}

          {status === 'resent' && (
            <div className="text-center py-6 space-y-3" data-testid="verify-resent">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
              <p className="text-sm text-gray-600">Verification email sent. Check your inbox.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
